import React, { useState, useRef } from 'react';
import { toast } from '../utils/toast';
import { googleSpeechService } from '../services/googleSpeechService';

export function VoiceRecorder({ onTranscriptionComplete, placeholder = 'Click to start recording...', className = '' }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const startRecording = async () => {
    try {
      // Request microphone access with specific constraints
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          sampleSize: 16,
          volume: 1.0
        }
      });
      
      streamRef.current = stream;
      
      // Check if the browser supports the required mime type
      const mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        throw new Error('Browser does not support the required audio format');
      }

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000
      });
      
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        setIsProcessing(true);
        try {
          if (chunksRef.current.length === 0) {
            throw new Error('No audio data recorded');
          }

          const audioBlob = new Blob(chunksRef.current, { type: mimeType });
          
          // Log the audio blob size and type for debugging
          console.log('Audio blob:', {
            size: audioBlob.size,
            type: audioBlob.type
          });

          const transcription = await googleSpeechService.transcribeAudio(audioBlob);
          
          if (transcription && transcription.trim()) {
            onTranscriptionComplete(transcription);
            toast.success('Audio transcribed successfully!');
          } else {
            throw new Error('No transcription received');
          }
        } catch (error) {
          console.error('Recording error:', error);
          toast.error(error.message || 'Failed to transcribe audio');
        } finally {
          setIsProcessing(false);
          // Clean up the media stream
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
        }
      };

      // Start recording with 1-second intervals for data
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      toast.info('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error(error.message || 'Failed to start recording');
      // Clean up if there was an error
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  };

  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        toast.info('Recording stopped');
      }
    } catch (error) {
      console.error('Error stopping recording:', error);
      toast.error('Failed to stop recording');
      setIsRecording(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={isRecording ? stopRecording : startRecording}
        disabled={isProcessing}
        className={`w-full px-4 py-3 rounded-lg border text-left transition-all duration-200 ${
          isRecording
            ? 'bg-red-50 border-red-500 text-red-700'
            : isProcessing
            ? 'bg-gray-100 border-gray-300 text-gray-500'
            : 'bg-white border-gray-300 hover:border-primary-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-500'
        }`}
      >
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${
            isRecording
              ? 'bg-red-500 animate-pulse'
              : isProcessing
              ? 'bg-gray-400'
              : 'bg-primary-500'
          }`} />
          <span>
            {isRecording
              ? 'Recording... Click to stop'
              : isProcessing
              ? 'Processing audio...'
              : placeholder}
          </span>
        </div>
      </button>
    </div>
  );
}

export default VoiceRecorder; 